import { FormProvider } from 'react-hook-form';
import { useTheme } from '../../../hooks/useTheme';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button } from '@dsi/react-eds';
import { useCustomerSettingsForm } from './use-customer-settings-form';
import { useScanningSettingsForm } from './use-scanning-settings-form';

import styles from './SwgwScanning.module.css';
import CustomerSettingsForm from './CustomerSettingsForm';
import ScanningSettings from './ScanningSettings';
import ProductNumberUI from './ProductNumber';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { useEffect, useState } from 'react';
import api from '../../../api/axiosInstance';
import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useLoader } from '../../../hooks/useLoader';

function SwgwScanning() {
  const { theme } = useTheme();
  const customerForm = useCustomerSettingsForm({});
  const scanningSettingsForm = useScanningSettingsForm();
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();
  const [defaultValues, setDefaultValues] = useState<object | null>(null);

  const swgwConfigurationData = async () => {
    try {
      showLoader();

      const resp = await api.get('/api/swgw-config');
      return resp.data;
    } catch (error: unknown) {
      // show error
      addNotifications([
        {
          title: 'Error - SWGW Configuration',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching the SWGW Configuration.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
      return Promise.reject(error);
    } finally {
      hideLoader();
    }
  };

  const swgwCredentialsData = async () => {
    try {
      showLoader();

      const resp = await api.get('/api/swgw-config/credentials');
      return resp.data;
    } catch (error: unknown) {
      // show error
      addNotifications([
        {
          title: 'Error - SWGW Credentials',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching SWGW Credentials',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    } finally {
      hideLoader();
    }
  };

  const handleCustomerSettingsSubmit = async () => {
    try {
      const body = {
        client_key: customerForm.getValues('client_key'),
        secret_key: btoa(String.fromCharCode(...new TextEncoder().encode(customerForm.getValues('client_secret'))))
      };
      showLoader();

      const resp = await api.post('/api/swgw-config/credentials', { ...body });
      addNotifications([
        {
          title: 'Success - SWGW Credentials',
          icon: 'icon-success',
          description: 'SWGW credentials saved successfully!',
          timestamp: new Date(),
          color: 'green'
        }
      ]);
      return resp;
    } catch (error) {
      addNotifications([
        {
          title: 'Error - SWGW Credentials',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error?.message
              : 'An error occurred while saving the SWGW credentials',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    } finally {
      hideLoader();
    }
  };

  const handleSaving = () => {
    handleCustomerSettingsSubmit();
  };

  const handleCronSettingsSubmit = async () => {
    try {
      showLoader();
      const body = { ...scanningSettingsForm.getValues() };

      const resp = await api.post('/api/swgw-config/daconfig', body);
      addNotifications([
        {
          title: 'Success - Execution Interval',
          icon: 'icon-success',
          description: 'Configuration saved successfully!',
          timestamp: new Date(),
          color: 'green'
        }
      ]);
      return resp;
    } catch (error) {
      addNotifications([
        {
          title: 'Error - Execution Interval',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while saving the configuration',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    } finally {
      hideLoader();
    }
  };

  const swgwCronJobData = async () => {
    try {
      showLoader();

      const resp = await api.get('/api/swgw-config/daconfig');
      return resp.data;
    } catch (error: unknown) {
      // show error
      addNotifications([
        {
          title: 'Error - SWGW Configuration',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching SWGW cron job configuration',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return Promise.reject(error);
    } finally {
      hideLoader();
    }
  };

  const { data: swgwConfiguration } = useQuery({
    queryKey: ['swgwConfiguration'],
    queryFn: swgwConfigurationData,
    retry: 0,
    refetchOnWindowFocus: false,
    throwOnError: false
  });
  const { data: swgwCredentials, error: credentialsError } = useQuery({
    queryKey: ['swgwCredentials'],
    queryFn: swgwCredentialsData,
    retry: 0,
    refetchOnWindowFocus: false
  });

  const { data: swgwCronJob, error: cronJobError } = useQuery({
    queryKey: ['swgwCronJob'],
    queryFn: swgwCronJobData,
    retry: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (swgwCronJob) {
      scanningSettingsForm.reset({ ...swgwCronJob });
    }
  }, [scanningSettingsForm, swgwCronJob]);

  useEffect(() => {
    const updatedFields = {
      euft: '',
      euft_name: '',
      client_key: '',
      client_secret: '',
      api_url: ''
    };

    if (swgwConfiguration) {
      updatedFields.euft = swgwConfiguration.euft;
      updatedFields.euft_name = swgwConfiguration.euft_name;
      updatedFields.api_url = swgwConfiguration.api_url;
    }

    if (swgwCredentials) {
      updatedFields.client_key = swgwCredentials.client_key;
      updatedFields.client_secret = new TextDecoder().decode(Uint8Array.from(atob(swgwCredentials.client_secret), (c) => c.charCodeAt(0)));
    }

    customerForm.reset({ ...updatedFields });
    setDefaultValues({ ...updatedFields });
  }, [customerForm, swgwConfiguration, swgwCredentials]);

  return (
    <div className={theme ? 'light-theme' : 'dark-theme'}>
      <FormProvider {...customerForm}>
        <form onSubmit={customerForm.handleSubmit(handleSaving)} className={styles.scrollableForm}>
          <div
            className={`${styles.settingsContainer} ${styles.settingsContainerPadding}  ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
          >
            <div className={styles.settingsTitleContainer}>
              <div className={styles.settingsTitle}>Software Gateway</div>
              <p className={styles.scanningDescription}>
                These settings help tailor the platform behavior and ensure accurate tracking and classification of onboarding data across
                customers and software types.
              </p>
            </div>

            <div className={styles.settingsContainerSize}>
              <CustomerSettingsForm />
            </div>
            {credentialsError ? (
              <></>
            ) : (
              <div className={styles.buttonContainer}>
                <ReactEDSWrapper
                  component={Button}
                  className="btn secondary"
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    showLoader();
                    customerForm.reset(defaultValues);
                    setTimeout(() => {
                      hideLoader();
                    }, 500);
                  }}
                >
                  Cancel
                </ReactEDSWrapper>
                <ReactEDSWrapper component={Button} className="btn primary" variant="primary" type="submit">
                  Save
                </ReactEDSWrapper>
              </div>
            )}
          </div>
        </form>
      </FormProvider>

      <FormProvider {...scanningSettingsForm}>
        <form onSubmit={scanningSettingsForm.handleSubmit(handleCronSettingsSubmit)} className={styles.scrollableForm}>
          <div
            className={`${styles.settingsContainer} ${styles.settingsContainerPadding}  ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
          >
            <ScanningSettings />
            {cronJobError ? (
              <></>
            ) : (
              <div className={styles.buttonContainer}>
                <ReactEDSWrapper
                  component={Button}
                  className="btn secondary"
                  variant="secondary"
                  type="button"
                  onClick={() => {
                    showLoader();
                    scanningSettingsForm.reset();
                    setTimeout(() => {
                      hideLoader();
                    }, 500);
                  }}
                >
                  Cancel
                </ReactEDSWrapper>
                <ReactEDSWrapper component={Button} className="btn primary" variant="primary" type="submit">
                  Save
                </ReactEDSWrapper>
              </div>
            )}
          </div>
        </form>
      </FormProvider>

      <div
        className={`${styles.settingsContainer} ${styles.settingsContainerPadding}  ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
      >
        <ProductNumberUI />
      </div>
    </div>
  );
}

export default SwgwScanning;
